import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Form, Modal, Select, message, Input, Tag, Row, Col, TreeSelect } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { getUserPage, createUser, updateUser, deleteUser, getUserRoles, assignUserRoles } from '../services/api/user';
import { getAllRoles } from '../services/api/role';
import { getDeptTree } from '../services/api/dept';
import type { UserParams, Gender, UserStatus, UserInfo } from '../types/user';
import type { Dept } from '../types/dept';
import SearchBar from '../components/common/SearchBar';
import EmptyState from '../components/common/EmptyState';

const { Option } = Select;
const { TreeNode } = TreeSelect;

const Users: React.FC = () => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedDeptId, setSelectedDeptId] = useState<number | undefined>(undefined);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [checkedRoleIds, setCheckedRoleIds] = useState<number[]>([]);
  const [allRoles, setAllRoles] = useState<any[]>([]);
  const [deptTree, setDeptTree] = useState<Dept[]>([]);

  // 获取用户列表
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUserPage({
        current,
        size: pageSize,
        keyword: searchText || '',
        deptId: selectedDeptId
      });
      setUsers(response.records || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取部门树
  const fetchDeptTree = async () => {
    try {
      const tree = await getDeptTree();
      setDeptTree(tree);
    } catch (error) {
      console.error('获取部门树失败:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAllRoles();
    fetchDeptTree();
  }, [current, pageSize, searchText, selectedDeptId]);

  // 获取所有角色
  const fetchAllRoles = async () => {
    try {
      const roles = await getAllRoles();
      setAllRoles(roles || []);
    } catch (error) {
      console.error('获取角色列表失败:', error);
    }
  };

  // 处理新增/编辑
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setModalVisible(false);

      if (editingUser && editingUser.id) {
        await updateUser({ id: editingUser.id, ...values });
        message.success('用户更新成功');
      } else {
        await createUser(values);
        message.success('用户创建成功');
      }

      form.resetFields();
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.message || '操作失败');
    }
  };

  // 显示新增模态框
  const showCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 显示编辑模态框
  const showEditModal = (user: UserInfo) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      nickname: user.nickname,
      email: user.email,
      phone: user.phone,
      gender: user.gender,
      status: user.status,
      deptId: user.deptId,
    });
    setModalVisible(true);
  };

  // 删除用户
  const handleDelete = async (user: UserInfo) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除用户 "${user.username}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          if (user.id) {
            await deleteUser(user.id);
            message.success('用户删除成功');
            fetchUsers();
          }
        } catch (error: any) {
          console.error('删除失败:', error);
        }
      },
    });
  };

  // 显示角色分配模态框
  const showRoleModal = async (user: UserInfo) => {
    setSelectedUser(user);
    setRoleModalVisible(true);
    try {
      const roleIds = await getUserRoles(user.id!);
      setCheckedRoleIds(roleIds || []);
    } catch (error) {
      console.error('获取用户角色失败:', error);
      setCheckedRoleIds([]);
    }
  };

  // 保存角色分配
  const handleSaveRoles = async () => {
    if (!selectedUser) return;

    try {
      await assignUserRoles(selectedUser.id!, checkedRoleIds);
      message.success('角色分配成功');
      setRoleModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error('分配角色失败:', error);
      message.error('分配角色失败');
    }
  };

  // 性别映射
  const getGenderText = (gender?: Gender) => {
    const genderMap: Record<number, string> = {
      0: '未知',
      1: '男',
      2: '女',
    };
    return genderMap[gender || 0] || '未知';
  };

  // 状态映射
  const getStatusText = (status?: UserStatus) => {
    return status === 1 ? '启用' : '禁用';
  };

  // 将部门树转换为 TreeSelect 所需的格式
  const convertToTreeData = (nodes: Dept[]): any[] => {
    return nodes.map(node => ({
      title: node.name,
      value: node.id,
      key: node.id,
      children: node.children ? convertToTreeData(node.children) : []
    }));
  };

  const columns = [
    {
      title: '👤 用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '👤 姓名',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname: string) => nickname || '-',
    },
    {
      title: '🏢 所属部门',
      dataIndex: 'deptName',
      key: 'deptName',
      render: (deptName: string) => deptName || <span style={{ color: '#999' }}>未分配</span>,
    },
    {
      title: '📧 邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: '📱 手机',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: '👤 性别',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender: Gender) => (
        <Tag color={gender === 1 ? 'blue' : gender === 2 ? 'pink' : 'default'}>
          {getGenderText(gender)}
        </Tag>
      ),
    },
    {
      title: '🎭 角色',
      dataIndex: 'roles',
      key: 'roles',
      width: 200,
      render: (roles: string, record: UserInfo) => (
        <Space size="small">
          <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => showRoleModal(record)}>
            {roles || '未分配'}
          </Tag>
          <Button type="link" size="small" onClick={() => showRoleModal(record)}>
            配置
          </Button>
        </Space>
      ),
    },
    {
      title: '📊 状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 150,
      render: (_: any, record: UserInfo) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <Card
        title={
          <Space>
            <UserOutlined />
            <span>用户管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showCreateModal}
          >
            新增用户
          </Button>
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <SearchBar
            placeholder="搜索用户姓名或用户名..."
            onSearch={(value) => {
              setSearchText(value);
              setCurrent(1);
            }}
            width={300}
          />
          <TreeSelect
            style={{ width: 200 }}
            placeholder="选择部门筛选"
            treeData={convertToTreeData(deptTree)}
            value={selectedDeptId}
            onChange={(value) => setSelectedDeptId(value)}
            allowClear
            treeDefaultExpandAll
          />
        </div>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size || 10);
            }
          }}
          size="middle"
          locale={{ emptyText: <EmptyState description="暂无用户数据" /> }}
        />
      </Card>

      <Modal
        title={editingUser ? '✏️ 编辑用户' : '➕ 新增用户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={confirmLoading}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="👤 用户名"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="🔒 密码"
              rules={[{ required: true, message: '请输入密码!' }]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Form.Item
            name="nickname"
            label="👤 昵称"
          >
            <Input placeholder="请输入昵称" />
          </Form.Item>

          <Form.Item
            name="email"
            label="📧 邮箱"
          >
            <Input placeholder="example@email.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="📱 手机号"
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>

          <Form.Item
            name="deptId"
            label="🏢 所属部门"
          >
            <TreeSelect
              placeholder="请选择所属部门"
              treeData={convertToTreeData(deptTree)}
              allowClear
              treeDefaultExpandAll
            />
          </Form.Item>

          <Form.Item
            name="gender"
            label="👤 性别"
            initialValue={0}
          >
            <Select placeholder="请选择性别">
              <Option value={0}>未知</Option>
              <Option value={1}>男</Option>
              <Option value={2}>女</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="📊 状态"
            initialValue={1}
          >
            <Select placeholder="请选择状态">
              <Option value={0}>禁用</Option>
              <Option value={1}>启用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 角色分配模态框 */}
      <Modal
        title={`⚙️ 分配角色 - ${selectedUser?.username || ''}`}
        open={roleModalVisible}
        onOk={handleSaveRoles}
        onCancel={() => setRoleModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form layout="vertical">
          <Form.Item label="选择角色">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={checkedRoleIds}
              onChange={(values) => setCheckedRoleIds(values as number[])}
              placeholder="请选择角色"
            >
              {allRoles.map(role => (
                <Option key={role.id} value={role.id}>
                  {role.name} ({role.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
